<template>
  <layout-basic>
    <page-hero
      eyebrow="IMPROVE Live"
      title="A practical live session for growing businesses launching IMPROVE"
      lead="Join founder Steve Walker and the IMPROVE team for a hands-on session on rolling out onboarding, compliance and development — so your people are ready from day one."
    />

    <section class="live-intro">
      <v-container>
        <div class="live-intro__copy">
          <p>
            IMPROVE Live is a practical, founder-led session for leaders who are
            evaluating or launching IMPROVE. You’ll see how growing businesses
            get teams onboarded, keep compliance on track and build a simple
            development culture — then leave with a clear path to go live.
          </p>
          <p>
            There is no booking widget on this page. Start a 14-day trial and
            you’ll get the interactive tour first. If that isn’t enough, IMPROVE
            Live is offered in the same account — no separate signup.
          </p>
        </div>
      </v-container>
    </section>

    <point-grid title="What you’ll get" :points="points" :columns="2" tint />

    <section class="live-path">
      <v-container class="live-path__inner">
        <h2 class="font-sora">How to join IMPROVE Live</h2>
        <p>
          Create your trial, take the tour, then choose Join IMPROVE Live if you
          want a session with the team.
        </p>
        <div class="live-path__actions">
          <a :href="trialHref" class="live-btn live-btn--primary" rel="noopener">
            Start 14-day trial
          </a>
          <router-link :to="experienceHref" class="live-btn live-btn--teal">
            Experience IMPROVE
          </router-link>
          <router-link to="/contact" class="live-btn live-btn--ghost">
            Contact us
          </router-link>
        </div>
      </v-container>
    </section>

    <standard-cta
      title="Ready to launch with confidence?"
      lead="Start a 14-day trial to take the tour and join IMPROVE Live, or buy IMPROVE online when you’re ready."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import { marketingPageMeta } from '@/utils/marketingPage';
import {
  getTrialSignupHref,
  getExperienceHref,
} from '@/config/ctas';

export default {
  name: 'ImproveLive',
  components: { LayoutBasic, PageHero, PointGrid, StandardCta },
  metaInfo() {
    return marketingPageMeta({
      title: 'IMPROVE Live | IMPROVE',
      description:
        'Join IMPROVE Live — a practical founder-led session on how growing businesses launch onboarding, compliance and employee development.',
      path: '/improve-live',
    });
  },
  data() {
    return {
      points: [
        'How to roll out IMPROVE across your organisation',
        'Tips for onboarding and compliance from day one',
        'How Success supports your Launch Programme',
        'Real examples from growing businesses going live',
        'What to do in your first 14 days on trial',
        'Live Q&A with the IMPROVE team',
      ],
    };
  },
  computed: {
    trialHref() {
      return getTrialSignupHref();
    },
    experienceHref() {
      return getExperienceHref();
    },
  },
};
</script>

<style scoped>
.live-intro {
  padding: 8px 0 16px;
}

.live-intro__copy {
  max-width: 720px;
  margin: 0 auto;
  color: var(--brand-navy, #002458);
  font-size: 17px;
  line-height: 1.6;
  text-align: center;
}

.live-intro__copy p {
  margin: 0 0 14px;
}

.live-intro__copy p:last-child {
  margin-bottom: 0;
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
}

.live-path {
  padding: 48px 0 24px;
  text-align: center;
}

.live-path__inner {
  max-width: 720px;
}

.live-path h2 {
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
}

.live-path p {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 24px;
}

.live-path__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.live-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
}

.live-btn--primary {
  background: var(--brand-navy, #002458);
  color: #fff;
}

.live-btn--primary:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.live-btn--teal {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.live-btn--teal:hover {
  background: var(--brand-teal-dark, #078f96);
  color: #fff;
}

.live-btn--ghost {
  background: #fff;
  color: var(--brand-navy, #002458);
  border: 1.5px solid #d5dde8;
}

.live-btn--ghost:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-teal, #08a0a8);
}
</style>
